import React from 'react';
import { AlertTriangleIcon, RefreshCwIcon } from 'lucide-react';
import { Button } from '../ui/Button';
export type WidgetState = 'loading' | 'ready' | 'empty' | 'error';
interface WidgetProps {
  title: string;
  description?: string;
  action?: React.ReactNode;
  state?: WidgetState;
  /** Shown when `state` is `empty`. Says what is absent, never a fake number. */
  emptyTitle?: string;
  emptyDescription?: string;
  /** Shown when `state` is `error`, alongside a retry when one is possible. */
  errorMessage?: string;
  onRetry?: () => void;
  /** Provenance or a limit of the measurement. Rendered under the content. */
  footnote?: string;
  className?: string;
  children?: React.ReactNode;
}

/**
 * One dashboard widget. Every widget declares its own state, because a
 * dashboard where a single failing panel blanks the page is worse than no
 * dashboard: the administrator cannot tell what is broken from what is empty.
 *
 * `empty` and `error` are deliberately different. Empty means the platform has
 * nothing to report and that is the truth; error means we do not know, and
 * saying nothing would read as zero.
 */
export function Widget({
  title,
  description,
  action,
  state = 'ready',
  emptyTitle = 'No data available',
  emptyDescription,
  errorMessage,
  onRetry,
  footnote,
  className = '',
  children
}: WidgetProps) {
  return <section className={`flex min-w-0 flex-col rounded-card border border-line bg-surface ${className}`} aria-busy={state === 'loading' || undefined} data-id="element-440" data-mp="2ij1g3siesad1">
      <header className="flex flex-wrap items-start justify-between gap-3 border-b border-line px-4 py-3.5" data-id="element-441" data-mp="3a6thdaoocgzm">
        <div className="min-w-0" data-id="element-442" data-mp="2wg6qg4p5nve3">
          <h3 className="text-body font-semibold text-ink" data-id="element-443" data-mp="31ft60ksghnq2">{title}</h3>
          {description && <p className="mt-0.5 max-w-[52ch] text-caption leading-5 text-ink-muted" data-id="element-444" data-mp="3p5drpbfzuz31">
              {description}
            </p>}
        </div>
        {action && state === 'ready' && <div className="shrink-0" data-id="element-445" data-mp="3txx3q3z19m5q">{action}</div>}
      </header>

      <div className="min-w-0 flex-1 px-4 py-4" data-id="element-446" data-mp="7ldlm92ufjot">
        {state === 'loading' && <WidgetSkeleton data-id="element-447" data-mp="2nbztge1969n" />}

        {state === 'error' && <div className="flex flex-col items-start gap-3" data-id="element-448" data-mp="2lr6tv7d9vdw">
            <p className="flex items-start gap-2 text-body-sm text-ink" data-id="element-449" data-mp="3tslh8jnyoc7n">
              <AlertTriangleIcon className="mt-0.5 h-4 w-4 shrink-0 text-danger-700" aria-hidden="true" data-id="element-450" data-mp="3ovldifel3ljp" />
              <span data-id="element-451" data-mp="3kvb9376fx36y">{errorMessage ?? 'This panel could not be loaded.'}</span>
            </p>
            {onRetry && <Button variant="secondary" size="sm" icon={<RefreshCwIcon className="h-4 w-4" data-id="element-453" data-mp="3b12kuvofzwel" />} onClick={onRetry} data-id="element-452" data-mp="3b3eyiwn63klu">
                Try again
              </Button>}
          </div>}

        {state === 'empty' && <div className="py-2" data-id="element-454" data-mp="2x3463p67lxyk">
            <p className="text-body-sm font-medium text-ink" data-id="element-455" data-mp="2x4xfvdc4cyas">{emptyTitle}</p>
            {emptyDescription && <p className="mt-1 max-w-[52ch] text-caption leading-5 text-ink-muted" data-id="element-456" data-mp="1clbsxkx6i0hz">
                {emptyDescription}
              </p>}
          </div>}

        {state === 'ready' && children}
      </div>

      {footnote && state === 'ready' && <p className="border-t border-line px-4 py-2.5 text-caption leading-5 text-ink-subtle" data-id="element-457" data-mp="1qkbcwf9qxr7t">
          {footnote}
        </p>}
    </section>;
}
function WidgetSkeleton() {
  return <div className="space-y-2.5" aria-hidden="true" data-id="element-458" data-mp="xxqnvlgqtr7f">
      {[70, 92, 55].map((width, index) => <div key={index} className="cz-skeleton h-4 rounded bg-canvas" style={{
      width: `${width}%`
    }} data-id="element-459" data-mp="12uqupylf2xzl" data-mpi={index} />)}
    </div>;
}
interface BoundaryProps {
  title: string;
  children: React.ReactNode;
}

/**
 * Contains a widget's failure to that widget. Without this, one thrown render
 * takes the whole console down — which is the opposite of what a monitoring
 * surface is for.
 */
export class WidgetBoundary extends React.Component<BoundaryProps, {
  failed: boolean;
}> {
  constructor(props: BoundaryProps) {
    super(props);
    this.state = {
      failed: false
    };
  }
  static getDerivedStateFromError() {
    return {
      failed: true
    };
  }
  componentDidCatch(error: unknown) {
    // Recorded for the operator, not swallowed.
    console.error('[admin widget]', this.props.title, error);
  }
  render() {
    if (this.state.failed) {
      return <Widget title={this.props.title} state="error" errorMessage="This panel stopped responding. The rest of the console is unaffected." onRetry={() => this.setState({
        failed: false
      })} data-id="element-460" data-mp="2vl2psorb0rhj" />;
    }
    return this.props.children;
  }
}